import { useTranslation } from 'react-i18next';
import { useStandingOrderOptions } from '../../../api/standing-orders';
import { Button, FormContentIsland, HookForm, Stack } from '@razz-team/ui-web';
import { useForm } from 'react-hook-form';
import { useCallback, useEffect, useMemo } from 'react';
import { NetworkField } from '../../Field/NetworkField';
import { AccountNumberField } from '../../Field/AccountNumberField';
import { AmountField } from '../../Field/AmountField';
import { formatFiatAccountNumberForServer } from '@razz-team/utils';
import { CurrencyField } from '../../Field/CurrencyField';
import { useFormError } from '../../../hooks/useFormError';
import {
  createStandingOrderAmount,
  createStandingOrderOriginFormValues,
  StandingOrderOriginFormValues,
} from './utils';
import { UseMutationResult } from '@tanstack/react-query';
import { ApiCreateStandingOrderRequest, ApiError, ApiStandingOrder } from '../../../openapi';

const sepaAccountNumberFormat = { type: 'IBAN', mask: '' };

export function StandingOrderOriginStep({
  ref,
  order,
  mutation,
}: {
  ref?: React.Ref<HTMLDivElement>;
  order?: ApiStandingOrder;
  mutation: UseMutationResult<unknown, ApiError, ApiCreateStandingOrderRequest>;
}) {
  const { t } = useTranslation();

  const optionsQuery = useStandingOrderOptions();

  const form = useForm<StandingOrderOriginFormValues>({
    defaultValues: createStandingOrderOriginFormValues({ order: order }),
  });
  const [currency, networkId] = form.watch(['currency', 'networkId']);

  useFormError(form, 'accountNumber', mutation.error);

  useFormError(form, 'currency', optionsQuery.error);

  const currencies = useMemo(() => Object.keys(optionsQuery.data ?? {}), [optionsQuery.data]);
  const networks = useMemo(
    () => (currency && optionsQuery.data ? optionsQuery.data?.[currency] : []),
    [currency, optionsQuery.data],
  );

  // set initial currency and network if not set
  useEffect(() => {
    const currencyValue = form.getValues('currency');
    if (currencyValue === null && optionsQuery.data) {
      for (const [currency, networks] of Object.entries(optionsQuery.data)) {
        if (networks.length) {
          form.setValue('currency', currency);
          form.setValue('networkId', networks[0]);
          break;
        }
      }
    }
  }, [form, optionsQuery.data]);

  // set first available network
  useEffect(() => {
    if (networkId === null || !networks.includes(networkId)) {
      form.setValue('networkId', networks[0] ?? null);
    }
  }, [form, networkId, networks]);

  const accountNumberFormat = networkId === 'SEPA' ? sepaAccountNumberFormat : undefined;

  const handleSubmit = useCallback(
    (values: StandingOrderOriginFormValues) => {
      if (!mutation.isPending && values.currency && values.networkId) {
        mutation.mutate({
          predicate: {
            origin: {
              networkId: values.networkId,
              accountNumber: formatFiatAccountNumberForServer(values.accountNumber),
            },
            amount: createStandingOrderAmount({
              minAmount: values.minAmount,
              maxAmount: values.maxAmount,
              currency: values.currency,
            }),
          },
        });
      }
    },
    [mutation],
  );

  return (
    <Stack
      ref={ref}
      width="100%"
      spacing={4}
      component={HookForm}
      form={form}
      onSubmit={handleSubmit}
    >
      <FormContentIsland title={t('Origin')}>
        <CurrencyField
          name="currency"
          label={t('Currency')}
          required
          disableClearable
          options={currencies}
          loading={optionsQuery.isPending}
        />
        <NetworkField
          name="networkId"
          label={t('Network')}
          options={networks}
          loading={optionsQuery.isPending}
          required
          disableClearable
        />
        <AccountNumberField
          name="accountNumber"
          autoComplete="off"
          required
          format={accountNumberFormat}
          autoFocus
        />
        <Stack direction="row" spacing={2}>
          <AmountField
            name="minAmount"
            label={t('Minimum amount (optional)')}
            currency={currency}
            fullWidth
          />
          <AmountField
            name="maxAmount"
            label={t('Maximum amount (optional)')}
            currency={currency}
            fullWidth
          />
        </Stack>
      </FormContentIsland>
      <Button type="submit" size="large" color="primary" loading={mutation.isPending} fullWidth>
        {order ? t('Update') : t('Continue')}
      </Button>
    </Stack>
  );
}
